import React, { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { useHistory } from 'react-router-dom';
import { MenuOutlined } from '@ant-design/icons';
import { Button, Col, Dropdown, Row, Tooltip } from 'antd';

import { useDeleteBeraJobSummaryLineage } from '@/hooks';
import { ModalDelete } from '@/components/ui/Modals/ModalDelete';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useDownloadPDF } from '@/components/views/BeraJob/Results/hooks';
import { DetailIcon, DownloadIcon, EditIcon, PlusCircleIcon, TrashIcon } from '@/assets/icons/v2';
import { ACTION_ICON_SIZE, ACTIONS_GUTTER, BERA_REPORT_PATH, BERA_VERSION_V2 } from '@/views/Bera/constants';
import { ModalRevision } from '@/views/Bera/_components/ModalRevision';
import { ModalFullRevision } from '@/views/Bera/_components/ModalFullRevision';

import type { BeraJobSummaryListItem } from '@/types/Bera';
import * as S from './styles';

interface ColumnActionsProps {
	report: BeraJobSummaryListItem;
	onShowVersions: (report: BeraJobSummaryListItem) => void;
}

export function ColumnActions({ report, onShowVersions }: Readonly<ColumnActionsProps>): JSX.Element {
	const history = useHistory();
	const { organization, company } = useApplicationContext();

	const [isRevisionOpen, setIsRevisionOpen] = useState<boolean>(false);
	const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);

	const { mutate: downloadPDF, isLoading: isDownloading } = useDownloadPDF();
	const { mutate: deleteJobSummary, isLoading: isDeleting } = useDeleteBeraJobSummaryLineage();

	const isVersionTwo = report.version === BERA_VERSION_V2;
	const isUnconsolidated = report.consolidated === false;
	const hasScore = report.overall_score !== null;

	let revisionTooltip = I18n.get('Create new revision');

	if (isUnconsolidated) {
		revisionTooltip = I18n.get('Consolidate the evaluation to start a revision');
	}

	function handleOpenRevision(): void {
		setIsRevisionOpen(true);
	}

	function handleCloseRevision(): void {
		setIsRevisionOpen(false);
	}

	function handleShowVersions(): void {
		onShowVersions(report);
	}

	function handleOpenDelete(): void {
		setIsDeleteOpen(true);
	}

	function handleCloseDelete(): void {
		setIsDeleteOpen(false);
	}

	function handleEdit(): void {
		history.push(`${BERA_REPORT_PATH}/${report.id}`);
	}

	function handleDownload(): void {
		const payload = {
			organization_id: organization?.id,
			company_id: company?.id,
			bera_job_summary_id: report.id,
			locale: window.navigator.language
		};
		downloadPDF(payload);
	}

	function handleConfirmDelete(): void {
		const payload = { id: report.id, organization_id: organization?.id, company_id: company?.id };

		deleteJobSummary(payload, { onSuccess: handleCloseDelete });
	}

	const actions = [
		{
			key: 'revision',
			danger: false,
			tooltip: revisionTooltip,
			icon: <PlusCircleIcon width={ACTION_ICON_SIZE} height={ACTION_ICON_SIZE} />,
			disabled: isUnconsolidated,
			onClick: handleOpenRevision
		},
		{
			key: 'versions',
			danger: false,
			tooltip: I18n.get('View versions'),
			icon: <DetailIcon width={ACTION_ICON_SIZE} height={ACTION_ICON_SIZE} />,
			disabled: !isVersionTwo,
			onClick: handleShowVersions
		},
		{
			key: 'edit',
			danger: false,
			tooltip: I18n.get('Edit'),
			icon: <EditIcon width={ACTION_ICON_SIZE} height={ACTION_ICON_SIZE} />,
			disabled: false,
			onClick: handleEdit
		},
		{
			key: 'download',
			danger: false,
			tooltip: I18n.get('Download PDF'),
			icon: <DownloadIcon width={ACTION_ICON_SIZE} height={ACTION_ICON_SIZE} />,
			disabled: !hasScore || isDownloading,
			onClick: handleDownload
		},
		{
			key: 'delete',
			danger: true,
			tooltip: I18n.get('Delete'),
			icon: <TrashIcon width={ACTION_ICON_SIZE} height={ACTION_ICON_SIZE} />,
			disabled: false,
			onClick: handleOpenDelete
		}
	];

	const menuItems = actions.map(({ key, tooltip, icon, disabled, danger, onClick }) => ({
		key,
		icon,
		danger,
		label: (
			<Button type="link" disabled={disabled} onClick={onClick}>
				{tooltip}
			</Button>
		)
	}));

	return (
		<>
			<Row align="middle" justify="center">
				<S.ActionsInline xs={24}>
					<Row align="middle" justify="center" gutter={[ACTIONS_GUTTER, 0]} wrap={false}>
						{actions.map(({ key, tooltip, icon, disabled, danger, onClick }) => (
							<Col key={key}>
								<Tooltip title={tooltip}>
									<S.ActionButton
										icon={icon}
										type="link"
										$danger={danger}
										onClick={onClick}
										disabled={disabled}
										aria-label={tooltip}
									/>
								</Tooltip>
							</Col>
						))}
					</Row>
				</S.ActionsInline>
				<S.ActionsMenu xs={2}>
					<Dropdown menu={{ items: menuItems }} trigger={['click']}>
						<Button type="link" icon={<MenuOutlined />} />
					</Dropdown>
				</S.ActionsMenu>
			</Row>
			{!!isRevisionOpen && !!isVersionTwo && (
				<ModalRevision open={isRevisionOpen} report={report} onCancel={handleCloseRevision} />
			)}
			{!!isRevisionOpen && !isVersionTwo && (
				<ModalFullRevision open={isRevisionOpen} report={report} onCancel={handleCloseRevision} />
			)}
			<ModalDelete
				open={isDeleteOpen}
				loading={isDeleting}
				onCancel={handleCloseDelete}
				onConfirm={handleConfirmDelete}
				description={I18n.get('Heads up! Want to delete this report and all of its versions?')}
			/>
		</>
	);
}
